import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  Users, ArrowUpDown, Wallet, BadgeDollarSign,
  Award, TrendingUp, UserCheck, UserX, CreditCard, DollarSign
} from 'lucide-react';
import {
  Card, CardContent, Typography, Box, Grid, Skeleton,
  CircularProgress, Dialog, DialogTitle, DialogContent, IconButton,
  Select, MenuItem, FormControl, InputLabel, ButtonGroup, Button, Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, darken } from '@mui/material/styles';
import Chart from 'react-apexcharts';

import api from '../utils/api';
import { API_BASE } from '../config';

const fx = async <T,>(endpoint: string): Promise<T | null> => {
  try {
    // Relative path or full URL both work with our axios instance if configured correctly
    // or we can just pass the path if it starts with /api
    const path = endpoint.startsWith(API_BASE) ? endpoint.substring(API_BASE.length) : endpoint;
    const res = await api.get(path);
    return res.data as T;
  } catch (error) {
    console.error(`FX Error: ${endpoint}`, error);
    return null;
  }
};

interface DashboardData {
  status: string;
  data: {
    stats: {
      totalUsers: number;
      walletBalance: number;
      totalDeposits: number;
      totalWithdrawals: number;
      totalTransfers: number;
      normalInvestment: number;
      platinumInvestment: number;
      activeInvestorsCount: number;
      inactiveInvestorsCount: number;
    };
    chartData: any[];
    pieData: { labels: string[]; series: number[] };
  }
}

const StatCard: React.FC<{ title: string; value: number; icon: any; color: string; loading: boolean; onClick?: () => void }> = ({
  title, value, icon: Icon, color, loading, onClick
}) => {
  const formattedValue = title.includes('Balance') || title.includes('Deposits') || title.includes('Withdrawals') ||
    title.includes('Transfers') || title.includes('Investment') || title.includes('Growth')
    ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : value.toLocaleString();

  return (
    <Card
      onClick={onClick}
      elevation={4}
      sx={{
        p: 2.5, borderRadius: 3, cursor: onClick ? 'pointer' : 'default',
        background: `linear-gradient(135deg, ${alpha(color, .12)}, ${alpha(darken(color, .2), .12)})`,
        transition: 'transform .25s',
        '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 3 } : undefined
      }}
    >
      {loading ? <Skeleton variant="rectangular" height={76} /> : (
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', p: 0 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
            <Typography variant="h5" fontWeight={800}>{formattedValue}</Typography>
          </Box>
          <Box sx={{ p: 1.2, bgcolor: alpha(color, .18), borderRadius: 2 }}>
            <Icon size={30} color={color} />
          </Box>
        </CardContent>
      )}
    </Card>
  );
};

const EmailDialog: React.FC<{ type: 'active' | 'inactive' | null; onClose: () => void }> = ({ type, onClose }) => {
  const [emails, setEmails] = useState<string[] | null>(null);
  useEffect(() => {
    if (!type) return;
    setEmails(null);
    fx<string[]>(`${API_BASE}/api/email/${type}-investors`).then(setEmails);
  }, [type]);

  return (
    <Dialog open={!!type} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {type === 'active' ? 'Active' : 'Inactive'} Investor Emails
        <Box flexGrow={1} />
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 400 }}>
        {emails === null ? <Skeleton variant="rectangular" height={120} /> : emails.length ? (
          <Box component="ul" sx={{ pl: 2 }}>{emails.map(e => <li key={e}><Typography variant="body2">{e}</Typography></li>)}</Box>
        ) : <Typography>No data.</Typography>}
      </DialogContent>
    </Dialog>
  );
};

const currency = (v: number) => `$${v.toLocaleString()}`;

const ChartCard: React.FC<{ title: string; variant: 'line' | 'area' | 'bar' | 'pie'; data: any[]; pieData?: any }> = ({ title, variant, data, pieData }) => {
  let chartEl: React.ReactElement;
  if (variant === 'line') {
    chartEl = (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} /><XAxis dataKey="name" /><YAxis tickFormatter={currency} />
        <Tooltip formatter={currency} /><Line dataKey="deposits" stroke="#523680" strokeWidth={3} /><Line dataKey="withdrawals" stroke="#FF6B6B" strokeWidth={3} />
      </LineChart>
    );
  } else if (variant === 'area') {
    chartEl = (
      <AreaChart data={data}>
        <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#523680" stopOpacity={0.15} /><stop offset="95%" stopColor="#523680" stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} /><XAxis dataKey="name" /><YAxis tickFormatter={currency} /><Tooltip formatter={currency} />
        <Area dataKey="deposits" stroke="#523680" fill="url(#g)" strokeWidth={3} />
      </AreaChart>
    );
  } else if (variant === 'bar') {
    chartEl = (
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} /><XAxis dataKey="name" /><YAxis tickFormatter={currency} /><Tooltip formatter={currency} />
        <Bar dataKey="transfers" fill="#523680" radius={[4, 4, 0, 0]}>{data.map((_, i) => <Cell key={i} fill={i % 2 ? '#6B4A99' : '#523680'} />)}</Bar>
      </BarChart>
    );
  } else {
    chartEl = <Chart options={{ chart: { type: 'donut' }, labels: pieData?.labels, legend: { position: 'bottom' }, stroke: { show: false }, plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: 'Investments' } } } } }, tooltip: { y: { formatter: (val: number) => `$${val.toLocaleString()}` } } }} series={pieData?.series || []} type="donut" />;
  }
  return <Card elevation={4} sx={{ p: 3 }}><Typography variant="subtitle1" fontWeight={700} mb={1}>{title}</Typography><ResponsiveContainer width="100%" height={320}>{chartEl}</ResponsiveContainer></Card>;
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData['data'] | null>(null);
  const [dialog, setDialog] = useState<'active' | 'inactive' | null>(null);
  const [range, setRange] = useState('all');
  const [groupBy, setGroupBy] = useState<'day' | 'month'>('month');

  useEffect(() => {
    setLoading(true);
    let url = `${API_BASE}/api/dashboard/summary?groupBy=${groupBy}`;

    if (range !== 'all') {
      const end = new Date();
      let start = new Date();
      if (range === 'today') start.setHours(0, 0, 0, 0);
      else if (range === '7d') start.setDate(end.getDate() - 7);
      else if (range === '30d') start.setDate(end.getDate() - 30);
      else if (range === 'month') {
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
      }
      url += `&startDate=${start.toISOString()}&endDate=${end.toISOString()}`;
    }

    fx<DashboardData>(url).then(res => {
      if (res?.status === 'success') {
        setData(res.data);
      }
      setLoading(false);
    });
  }, [range, groupBy]);

  // Small UX: Auto-switch grouping based on range
  useEffect(() => {
    if (['today', '7d', '30d'].includes(range)) {
      setGroupBy('day');
    } else {
      setGroupBy('month');
    }
  }, [range]);

  if (loading || !data) {
    return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}><CircularProgress size={60} /><Typography sx={{ mt: 2 }}>Loading Dashboard...</Typography></Box>;
  }

  const { stats, chartData, pieData } = data;
  const statList = [
    { key: 'u', title: 'Total Users', value: stats.totalUsers, icon: Users, color: '#673ab7', path: '/users' },
    { key: 'wb', title: 'Wallet Balance (System)', value: stats.walletBalance, icon: DollarSign, color: '#10B981', path: '/wallet' },
    { key: 'd', title: 'Deposits', value: stats.totalDeposits, icon: CreditCard, color: '#00C48C', path: '/deposit' },
    { key: 'w', title: 'Withdrawals', value: stats.totalWithdrawals, icon: ArrowUpDown, color: '#f44336', path: '/withdraw' },
    { key: 't', title: 'Transfers', value: stats.totalTransfers, icon: Wallet, color: '#2196f3', path: '/transferDetails' },
    { key: 'ni', title: 'Normal Investment', value: stats.normalInvestment, icon: BadgeDollarSign, color: '#9C27B0', path: '/invest' },
    { key: 'pi', title: 'Platinum Investment', value: stats.platinumInvestment, icon: Award, color: '#ff9800', path: '/platinumData' },
    { key: 'mg', title: 'Period Growth', value: stats.totalDeposits - stats.totalWithdrawals, icon: TrendingUp, color: '#4caf50' },
    { key: 'ai', title: 'Active Investors', value: stats.activeInvestorsCount, icon: UserCheck, color: '#10B981', dialog: 'active' as const },
    { key: 'ii', title: 'Inactive Investors', value: stats.inactiveInvestorsCount, icon: UserX, color: '#FF1744', dialog: 'inactive' as const },
    { key: 'sa', title: 'Amount Shifted', value: (stats as any).totalShiftAmount || 0, icon: ArrowUpDown, color: '#3f51b5', path: '/shiftHistory' }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" fontWeight={800} color="text.primary">Dashboard</Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <ButtonGroup variant="contained" size="small" sx={{ bgcolor: 'white', boxShadown: 1 }}>
            {[
              { id: 'today', label: 'Today' },
              { id: '7d', label: '7D' },
              { id: '30d', label: '30D' },
              { id: 'month', label: 'MTD' },
              { id: 'all', label: 'All Time' }
            ].map(b => (
              <Button
                key={b.id}
                onClick={() => setRange(b.id)}
                sx={{
                  bgcolor: range === b.id ? '#523680' : 'white',
                  color: range === b.id ? 'white' : '#523680',
                  '&:hover': { bgcolor: range === b.id ? '#6B4A99' : alpha('#523680', 0.05) }
                }}
              >
                {b.label}
              </Button>
            ))}
          </ButtonGroup>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Group By</InputLabel>
            <Select value={groupBy} label="Group By" onChange={(e) => setGroupBy(e.target.value as any)}>
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="month">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {statList.map(({ dialog: d, path, ...s }) => (
              <Grid key={s.key} item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <StatCard
                  {...s}
                  loading={false}
                  onClick={d ? () => setDialog(d) : (path ? () => navigate(path) : undefined)}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} lg={6}><ChartCard title="Deposits vs Withdrawals" variant="line" data={chartData} /></Grid>
            <Grid item xs={12} lg={6}><ChartCard title="Investment Growth" variant="area" data={chartData} /></Grid>
            <Grid item xs={12} lg={6}><ChartCard title="Transfer Volume" variant="bar" data={chartData} /></Grid>
            <Grid item xs={12} lg={6}><ChartCard title="Investment Split" variant="pie" data={chartData} pieData={pieData} /></Grid>
          </Grid>
        </>
      )}
      <EmailDialog type={dialog} onClose={() => setDialog(null)} />
    </Box>
  );
};

export default AdminDashboard;